import {forwardRef} from 'react';

import styled, {css} from 'styled-components';

import {body16Normal, smallRegular, tinyRegular} from '~/shared/theme/typography';
import {flipOnLTR, media} from '~/shared/theme/utils';

import FormInput from './FormInput';
import {FormInputProps} from './types';

const PIE_INPUT_VERTICAL_PADDING = 8;
const PIE_INPUT_HORIZONTAL_PADDING = 12;
const PIE_INPUT_LINE_HEIGHT = 20;

const pieInputTypography = css`
  ${body16Normal}
  ${media.minLargeMobile`
    ${smallRegular}
  `}
  line-height: ${PIE_INPUT_LINE_HEIGHT}px;
`;

const labelStyle = css<{float?: boolean}>`
  ${smallRegular}
  top: calc(100% - ${PIE_INPUT_LINE_HEIGHT}px - ${PIE_INPUT_VERTICAL_PADDING}px);
  line-height: ${PIE_INPUT_LINE_HEIGHT}px;
  ${flipOnLTR`
    right: ${PIE_INPUT_HORIZONTAL_PADDING}px;
    text-align: right;
  `}
  ${({float}) => float && `
    top: ${PIE_INPUT_VERTICAL_PADDING}px;
    ${tinyRegular}
  `};
`;

const StyledFormInput = styled(FormInput)`
  border-radius: 12px;
  border: 1px solid ${({theme, hasError}) => (hasError ? theme.colors.contentError : theme.colors.borderStrong)};
  padding: 8px ${PIE_INPUT_HORIZONTAL_PADDING}px;

  :hover {
    ${({theme, hasError}) => !hasError && `border-color: ${theme.colors.borderForm};`}
  }

  :focus-within {
    ${({theme, hasError}) => !hasError && `border-color: ${theme.colors.borderForm};`}
  }

  label {
    color: ${({theme}) => theme.colors.contentPlaceholder};
  }

  &&& input {
    ${pieInputTypography}
    color: ${({theme}) => theme.colors.contentDefault};
  }

  :has(input:disabled) {
    border-color: ${({theme}) => theme.colors.borderStrong};
    background-color: ${({theme}) => theme.colors.actionDisabled};

    label {
      color: ${({theme}) => theme.colors.contentDisabled};
    }

    &&& input {
      ${pieInputTypography}
      color: ${({theme}) => theme.colors.contentDisabled};

      ::placeholder {
        color: ${({theme}) => theme.colors.contentDisabled};
      }
    }
  }
`;

const FormPieInput = forwardRef<HTMLInputElement, FormInputProps & {isFocused?: boolean}>((props, ref) => {
  return (
    <StyledFormInput
      ref={ref}
      labelStyleOverrides={labelStyle}
      omitStarSymbolOnRequired
      {...props}
    />
  );
});

export default FormPieInput;
